import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import SectionHeading from './ui/SectionHeading';
import { Reveal } from './ui/Reveal';

const faqs = [
    {
        q: 'How long does it take to build an automation?',
        a: 'Most projects go live within 2–4 weeks. Simple workflows can ship in days, while complex, multi-system automations with custom AI agents may take a little longer. We give you a clear timeline after the discovery call.',
    },
    {
        q: 'Do I need technical knowledge to work with you?',
        a: 'Not at all. You bring the business problem — we handle every technical detail, from architecture to deployment. We also document everything and train your team so the system is easy to run and understand.',
    },
    {
        q: 'What tools and platforms do you work with?',
        a: 'We build on n8n, Make, and Zapier, and integrate with OpenAI, Anthropic, WhatsApp, HubSpot, Salesforce, Shopify, Supabase, Twilio, and 5,000+ other apps. If a tool has an API, we can automate it.',
    },
    {
        q: 'How much does it cost?',
        a: 'Pricing depends on scope and complexity. We offer fixed-price projects and ongoing retainers for maintenance and optimization. Most clients see the automation pay for itself within the first few months. Book a free audit for a tailored quote.',
    },
    {
        q: 'What happens after the automation is live?',
        a: 'We monitor your workflows 24/7, provide support, and continuously optimize as your business grows. You can choose a one-time build or an ongoing partnership — whichever fits your needs.',
    },
    {
        q: 'Is my data safe?',
        a: 'Yes. We follow security best practices, use encrypted connections and secrets management, and give you full ownership and control of your systems, credentials, and data at all times.',
    },
];

const FAQItem: React.FC<{ q: string; a: string; isOpen: boolean; onToggle: () => void }> = ({
    q,
    a,
    isOpen,
    onToggle,
}) => (
    <div className="rounded-2xl card-surface overflow-hidden transition-colors hover:border-white/[0.12]">
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/50 rounded-2xl"
            aria-expanded={isOpen}
        >
            <span className="font-display font-semibold text-white text-lg">{q}</span>
            <span className={`shrink-0 w-8 h-8 rounded-full bg-white/[0.05] border border-white/[0.08] grid place-items-center transition-all duration-300 ${isOpen ? 'rotate-45 bg-brand-cyan text-navy-900 border-brand-cyan' : 'text-brand-cyan'}`}>
                <Plus className="w-4 h-4" />
            </span>
        </button>
        <AnimatePresence initial={false}>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                >
                    <p className="px-6 pb-5 text-text-muted leading-relaxed">{a}</p>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

const FAQ: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    return (
        <section id="faq" className="relative py-24 md:py-32">
            <div className="container-x">
                <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-start">
                    <SectionHeading
                        align="left"
                        eyebrow="FAQ"
                        title={<>Questions? <span className="text-gradient">We've got answers.</span></>}
                        subtitle="Everything you need to know before we automate your business. Still curious? Reach out — we reply within 24 hours."
                        className="lg:sticky lg:top-28"
                    />

                    <Reveal className="space-y-3">
                        {faqs.map((faq, i) => (
                            <FAQItem
                                key={faq.q}
                                q={faq.q}
                                a={faq.a}
                                isOpen={openIndex === i}
                                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                            />
                        ))}
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default FAQ;
